import {
    computed,
    Directive,
    input,
    linkedSignal,
    output,
} from '@angular/core';
import type { DropAppearance } from './qpx-drop-surface';

/**
 * Event emitted when a draggable item is dropped on a dropzone.
 * @template T The type of data being dropped.
 */
export interface QpxDropEvent<T = unknown> {
    /** The data that was dropped */
    data: T;
    /** The drop effect that was applied */
    dropEffect: 'move' | 'copy' | 'link' | 'none';
    /** The native drag event */
    event: DragEvent;
    /** Whether the drop originated from outside the application */
    isExternal: boolean;
    /** Optional index of the drop location */
    index?: number;
    /** Optional type identifier for the dropped item */
    type?: string;
}

/**
 * Makes an element a dropzone that accepts draggable items.
 * Emits typed drop events with parsed data from DataTransfer.
 */
@Directive({
    selector: '[qpxDropzone]',
    host: {
        '[class.dndDragover]': 'isDragover() && !dndDisableDropIf()',
        '(dragover)': 'onDragOver($event)',
        '(dragleave)': 'onDragLeave($event)',
        '(drop)': 'onDrop($event)',
        '(window:blur)': 'clear()',
        '(window:dragend)': 'clear()',
        '(window:keydown.escape)': 'clear()',
    },
    exportAs: 'qpxDropzone',
})
export class QpxDropzone {
    // --- Inputs ---

    /** Conditionally disable drop functionality */
    public readonly dndDisableDropIf = input(false);
    public readonly dndAcceptedTypes = input<readonly string[] | null>(null);

    // --- Outputs ---

    /** Emitted when an item is dropped on this dropzone */
    public readonly qpxDrop = output<QpxDropEvent>();

    // --- State ---

    /** Whether an item is currently being dragged over this dropzone */
    public readonly isDragover = linkedSignal(() => {
        this.dndDisableDropIf();
        this.dndAcceptedTypes();
        return false;
    });

    // --- Event Handlers ---

    public readonly appearance = computed<DropAppearance | null>(() =>
        this.isDragover()
            ? { state: this.dndDisableDropIf() ? 'disabled' : 'valid' }
            : null
    );

    /** Handles dragover event */
    protected onDragOver(e: DragEvent): void {
        if (this.dndDisableDropIf() || !this.acceptsTypes(e)) {
            this.clear();
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        if (e.dataTransfer) {
            // Set drop effect based on what the draggable allows
            const effectAllowed = e.dataTransfer.effectAllowed;
            if (effectAllowed === 'all' || effectAllowed.includes('move')) {
                e.dataTransfer.dropEffect = 'move';
            } else if (effectAllowed.includes('copy')) {
                e.dataTransfer.dropEffect = 'copy';
            } else if (effectAllowed.includes('link')) {
                e.dataTransfer.dropEffect = 'link';
            } else {
                e.dataTransfer.dropEffect = 'none';
            }
        }

        this.isDragover.set(true);
    }

    /** Handles dragleave event */
    protected onDragLeave(e: DragEvent): void {
        // Only clear dragover if we're actually leaving the dropzone
        // (not just moving to a child element)
        const relatedTarget = e.relatedTarget as HTMLElement | null;
        const currentTarget = e.currentTarget as HTMLElement;

        if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
            this.isDragover.set(false);
        }
    }

    /** Handles drop event */
    protected onDrop(e: DragEvent): void {
        e.preventDefault();
        e.stopPropagation();
        this.isDragover.set(false);

        if (
            this.dndDisableDropIf() ||
            !e.dataTransfer ||
            !this.acceptsTypes(e)
        ) {
            return;
        }

        // Try to parse JSON data from DataTransfer
        let data: unknown = null;
        let isExternal = true;

        try {
            // Try application/json first
            const jsonData = e.dataTransfer.getData('application/json');
            if (jsonData) {
                data = JSON.parse(jsonData);
                isExternal = false;
            } else {
                // Fallback to text/plain
                const textData = e.dataTransfer.getData('text/plain');
                if (textData) {
                    data = JSON.parse(textData);
                    isExternal = false;
                }
            }
        } catch (error) {
            console.error('Failed to parse drop data:', error);
            return;
        }

        // Emit the drop event
        const dropEvent: QpxDropEvent = {
            data,
            dropEffect: e.dataTransfer.dropEffect as
                | 'move'
                | 'copy'
                | 'link'
                | 'none',
            event: e,
            isExternal,
        };

        this.qpxDrop.emit(dropEvent);
    }
    protected clear(): void {
        this.isDragover.set(false);
    }

    private acceptsTypes(event: DragEvent): boolean {
        const accepted = this.dndAcceptedTypes();
        return (
            accepted === null ||
            Array.from(event.dataTransfer?.types ?? []).some((type) =>
                accepted.includes(type)
            )
        );
    }
}
